import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authService } from '@/services';
import { clearAccessToken } from '@/api/tokenStorage';
import { ROLES } from '@/constants';
import { hasModulePermission } from '@/constants/permissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const authSessionRef = useRef(0);

  const isAuthenticated = Boolean(user);

  const initializeAuth = useCallback(async () => {
    const sessionId = authSessionRef.current;
    try {
      const result = await authService.refresh();
      if (sessionId !== authSessionRef.current) return;
      setUser(result.user);
    } catch {
      if (sessionId !== authSessionRef.current) return;
      clearAccessToken();
      setUser(null);
    } finally {
      if (sessionId === authSessionRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = useCallback(async (credentials) => {
    authSessionRef.current += 1;
    const result = await authService.login(credentials);
    setUser(result.user);
    setIsLoading(false);
    return result;
  }, []);

  const logout = useCallback(async () => {
    authSessionRef.current += 1;
    await authService.logout();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const profile = await authService.getMe();
    setUser(profile);
    return profile;
  }, []);

  const hasRole = useCallback(
    (...roles) => {
      if (!user) return false;
      if (user.role === ROLES.SUPER_ADMIN) return true;
      return roles.includes(user.role);
    },
    [user]
  );

  const hasPermission = useCallback(
    (module, action) => {
      if (!user) return false;
      if (user.role === ROLES.SUPER_ADMIN) return true;
      return hasModulePermission(user.permissions, module, action);
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      hasRole,
      hasPermission,
      refreshUser,
      setUser,
    }),
    [user, isAuthenticated, isLoading, login, logout, hasRole, hasPermission, refreshUser]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-muted border-t-primary" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
